import { createSelector } from 'reselect';
import type { DomCouponState } from '@travel/traveler-core/store/dom-coupon/types';
import { DomPaymentTypes } from '@travel/traveler-core/store/dom-coupon/types';

const normalizeBplRangeOld = (bplRangeOld?: boolean): boolean => bplRangeOld !== false;

/**
 * Selector factory that creates typed selectors for any RootState shape.
 * Usage: const selectors = createPromotionListSelectors((state) => state.promotionList);
 */
export const createDomCouponSelectors = <RootState>(
  selectSlice: (state: RootState) => DomCouponState,
) => {
  // Selectors that take providerId as parameter
  const getIsFetching = (providerId: number) =>
    createSelector(selectSlice, (state) => {
      return state.coupons[providerId]?.isFetching || false;
    });

  const getError = (providerId: number) =>
    createSelector(selectSlice, (state) => {
      return state.coupons[providerId]?.error || null;
    });

  const getData = (providerId: number) =>
    createSelector(selectSlice, (state) => {
      return state.coupons[providerId]?.data || null;
    });

  // Get all data (for debugging or bulk operations)
  const getAllData = createSelector(selectSlice, (state) => state.coupons);

  const createBonusProgramDiscountSelector = (paymentType?: DomPaymentTypes | null) =>
    createSelector(selectSlice, (state) => {
      const bp = state.bonusProgram?.data;
      if (!bp) return null;
      const minDiscountPercentage =
        paymentType === DomPaymentTypes.LOCAL
          ? bp.minDiscountPercentageForSearchLocal
          : bp.minDiscountPercentageForSearchCard;
      return { minDiscountPercentage, bplRangeOld: normalizeBplRangeOld(bp.bplRangeOld) };
    });

  const bonusProgramDiscountSelectorCache = new Map<
    DomPaymentTypes | 'default',
    ReturnType<typeof createBonusProgramDiscountSelector>
  >();

  const getBonusProgramDiscount = (paymentType?: DomPaymentTypes | null) => {
    const cacheKey = paymentType ?? 'default';
    const cachedSelector = bonusProgramDiscountSelectorCache.get(cacheKey);
    if (cachedSelector) return cachedSelector;

    const selector = createBonusProgramDiscountSelector(paymentType);
    bonusProgramDiscountSelectorCache.set(cacheKey, selector);

    return selector;
  };

  const getBonusProgramLocalDiscount = createSelector(selectSlice, (state) => {
    const bp = state.bonusProgram?.data;
    if (!bp) return null;
    return {
      minDiscountPercentageForSearchCard: bp.minDiscountPercentageForSearchCard,
      minDiscountPercentageForSearchLocal: bp.minDiscountPercentageForSearchLocal,
      bplRangeOld: normalizeBplRangeOld(bp.bplRangeOld),
    };
  });

  const getBonusProgramIsFetching = createSelector(
    selectSlice,
    (state) => state.bonusProgram?.isFetching ?? false,
  );

  const getBonusProgramError = createSelector(
    selectSlice,
    (state) => state.bonusProgram?.error ?? null,
  );

  return {
    getIsFetching,
    getError,
    getData,
    getAllData,
    getBonusProgramDiscount,
    getBonusProgramLocalDiscount,
    getBonusProgramIsFetching,
    getBonusProgramError,
  };
};
